//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr } from '@shared/SchemaTypes'

import { GridColDef, GridRowsProp } from '@mui/x-data-grid-premium'

import { ReadStream } from 'fs'

import { findStageConfiguration } from '@shared/DeploymentConfigs'
import { otAssert } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'
import axios from 'axios'
import { StageName } from '@shared/Enums'
import { User } from 'oidc-client-ts'
function bucketName(stageName: StageName): string {
  if (stageName === 'qasierra') {
    return 'otv5-qasierra-images-ot'
  }
  return `otv5-${stageName}-images`
}

export const urlForPdfFile = (stageName: StageName, fileName: string): string => {
  const str = `https://${bucketName(stageName)}.s3.amazonaws.com/${fileName}`

  logOT.info('Open file', str)
  return str
}
export const urlForFile = (stageName: StageName, fileName: string): string => {
  const str = `https://${bucketName(stageName)}.s3.amazonaws.com/${fileName}`

  logOT.trace('Open file', str)
  return str
}

export const uploadLogo = async (
  stageName: StageName,
  poolAddr: EthAddr,
  file: File | ReadStream,
  restAPIKey: string,
  cognitoUser: User
): Promise<void> => {
  const body = file
  const stageConfig = findStageConfiguration(stageName)

  const key = `${poolAddr}/logo.png`
  logOT.trace('AWS ', key, body)

  logOT.info('AWS uploadLogo ', key)

  logOT.info('AWS uploadLogo get Presigned', stageConfig.serverApiBaseUrl, key)

  const accessToken = cognitoUser?.access_token
  const result = await axios.post(
    `${stageConfig.serverApiBaseUrl}/uploadToS3Presigned`,
    {
      fileName: key,
      fileType: 'image/png',
    },
    {
      headers: {
        'x-api-key': restAPIKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  )
  const presignedUrl = result.data.url
  logOT.info('AWS uploadLogo get presignedUrl', presignedUrl)
  logOT.trace('AWS uploadLogo presignedUrl', presignedUrl)

  const resUpload = await axios.put(presignedUrl, file, {
    headers: {
      'Content-Type': 'image/png',
      // 'x-api-key': restAPIKey,
      // Authorization: `Bearer ${accessToken}`,
    },
  })

  logOT.info('AWS uploadLogo uploaded', !!file)
  otAssert(resUpload.status === 200, 'Upload failed')
  logOT.trace('AWS uploaded presignedUrl')

  const response = await axios.get(`https://${bucketName(stageConfig.stageName)}.s3.amazonaws.com/${key}`)
  otAssert(response.status === 200, 'Upload failed')
  logOT.trace('AWS uploaded verified')
}

export interface DocumentInfo {
  fileName: string
  modifiedDate: string
}
export const listDocuments = async (stageName: StageName, poolAddr: EthAddr): Promise<DocumentInfo[]> => {
  try {
    const url = `https://${bucketName(stageName)}.s3.amazonaws.com/?prefix=${poolAddr}`
    logOT.info('url', url)
    const response = await fetch(url)
    const xmlText = await response.text()
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml')
    logOT.trace('XMLDoc', xmlDoc)
    const allFiles = Array.from(xmlDoc.getElementsByTagName('Key')).map((key) => key.textContent) as string[]
    const allModifiedDate = Array.from(xmlDoc.getElementsByTagName('LastModified')).map((key) => key.textContent)

    const combined: DocumentInfo[] = allFiles.map((fileName, index): DocumentInfo => {
      return {
        fileName,
        modifiedDate: allModifiedDate[index] as string,
      }
    })

    logOT.trace('combined', combined)
    const pdfFiles = combined.filter((c) => c.fileName?.endsWith('.pdf')) ?? []
    logOT.trace('AWS listDocuments allFiles', allFiles)
    logOT.trace('AWS listDocuments pdfFiles', pdfFiles)

    return pdfFiles
  } catch (err) {
    logOT.error(err)
    return []
  }
}

export const listDocumentsLyt = async (stageName: StageName, poolAddr: EthAddr): Promise<DocumentInfo[]> => {
  try {
    const url = `https://${bucketName(stageName)}.s3.amazonaws.com/?prefix=${poolAddr}`
    logOT.info('url', url)
    const response = await fetch(url)
    const xmlText = await response.text()
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml')
    logOT.trace('XMLDoc', xmlDoc)
    const allFiles = Array.from(xmlDoc.getElementsByTagName('Key')).map((key) => key.textContent) as string[]
    const allModifiedDate = Array.from(xmlDoc.getElementsByTagName('LastModified')).map((key) => key.textContent)

    const combined: DocumentInfo[] = allFiles.map((fileName, index): DocumentInfo => {
      return {
        fileName,
        modifiedDate: allModifiedDate[index] as string,
      }
    })

    logOT.trace('combined', combined)
    const pdfFiles = combined.filter((c) => c.fileName?.endsWith('.pdf')) ?? []
    logOT.trace('AWS listDocumentsLyt allFiles', allFiles)
    logOT.trace('AWS listDocumentsLyt pdfFiles', pdfFiles)

    return pdfFiles
  } catch (err) {
    logOT.error(err)
    return []
  }
}

export const getPoolAssetCSV = async (
  stageName: StageName,
  poolAddr: EthAddr
): Promise<{ columns: GridColDef[]; rows: GridRowsProp } | undefined> => {
  try {
    const url = urlForFile(stageName, `${poolAddr}/assets.csv`)

    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/csv',
        'Content-Type': 'text/csv',
      },
    })
    const stripText = (text: string): string => {
      let value = text ? text.trim() : ''
      if (value.startsWith('"') && value.endsWith('"')) {
        // Remove leading and trailing quotes
        value = value.slice(1, -1)

        // Unescape escaped characters
        value = value.replace(/\\"/g, '"').replace(/\\\\/g, '\\')
      }
      value = value.replace(/\s/g, '')

      return value
    }
    const text = await resp.text()

    if (resp.status === 200 && text) {
      const csvHeaderName: string[] = text.slice(0, text.indexOf('\n')).split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
      const csvHeaderFields: string[] = text
        .slice(0, text.indexOf('\n'))
        .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
        .map(stripText)
      const columns: GridColDef[] = csvHeaderName.map((col: string) => {
        return { field: stripText(col), headerName: col, width: 150 }
      })
      const csvRows: string[] = text.slice(text.indexOf('\n') + 1).split('\n')

      const rows: Array<Record<string, string | number>> = csvRows.map((line: string, rowNum: number) => {
        const values = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)

        const row = csvHeaderFields.reduce(
          (rowObject: Record<string, string | number>, header: string, index: number) => {
            logOT.trace('header', header, index)
            rowObject[header] = stripText(values[index])
            return rowObject
          },
          {}
        )
        row.id = rowNum

        return row
      })

      return { columns, rows }
    } else {
      return {
        columns: [],
        rows: [],
      }
    }
  } catch {
    return {
      columns: [],
      rows: [],
    }
  }
}
export const uploadAssetsCsv = async (
  stageName: StageName,
  poolAddr: EthAddr,
  file: File,
  restAPIKey: string,
  cognitoUser: User
): Promise<void> => {
  const key = `${poolAddr}/assets.csv`
  const stageConfig = findStageConfiguration(stageName)

  const accessToken = cognitoUser?.access_token
  const result = await axios.post(
    `${stageConfig.serverApiBaseUrl}/uploadToS3Presigned`,
    {
      fileName: key,
      fileType: 'text/csv',
    },
    {
      headers: {
        'x-api-key': restAPIKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  )
  const presignedUrl = result.data.url

  const resUpload = await axios.put(presignedUrl, file, {})
  otAssert(resUpload.status === 200, 'Upload failed')
  otAssert(resUpload.status == 200)

  const response = await axios.get(`https://${bucketName(stageName)}.s3.amazonaws.com/${key}`)
  otAssert(response.status === 200, 'Upload failed')
}

export const uploadPdf = async (
  stageName: StageName,
  poolAddr: EthAddr,
  file: File,
  restAPIKey: string,
  cognitoUser: User
): Promise<void> => {
  const stageConfig = findStageConfiguration(stageName)
  const key = `${poolAddr}/${file.name}`
  const accessToken = cognitoUser?.access_token
  const result = await axios.post(
    `${stageConfig.serverApiBaseUrl}/uploadToS3Presigned`,
    {
      fileName: key,
      fileType: 'application/pdf',
    },
    {
      headers: {
        'x-api-key': restAPIKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  )
  const presignedUrl = result.data.url

  const resUpload = await axios.put(presignedUrl, file, { headers: { 'Content-Type': 'application/pdf' } })
  otAssert(resUpload.status === 200, 'Upload failed')

  const response = await axios.get(`https://${bucketName(stageName)}.s3.amazonaws.com/${key}`)
  otAssert(response.status === 200, 'Upload failed')
}

export const uploadPdfLyt = async (
  stageName: StageName,
  poolAddr: EthAddr,
  file: File,
  restAPIKey: string,
  cognitoUser: User
): Promise<void> => {
  const stageConfig = findStageConfiguration(stageName)
  const key = `${poolAddr}/${file.name}`
  const accessToken = cognitoUser?.access_token
  const result = await axios.post(
    `${stageConfig.serverApiBaseUrl}/uploadToS3Presigned`,
    {
      fileName: key,
      fileType: 'application/pdf',
    },
    {
      headers: {
        'x-api-key': restAPIKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  )
  const presignedUrl = result.data.url

  const resUpload = await axios.put(presignedUrl, file, { headers: { 'Content-Type': 'application/pdf' } })
  otAssert(resUpload.status === 200, 'Upload failed')

  const response = await axios.get(`https://${bucketName(stageName)}.s3.amazonaws.com/${key}`)
  otAssert(response.status === 200, 'Upload failed')
}

export const deletePdfLyt = async (
  stageName: StageName,
  poolAddr: EthAddr,
  fileName: string,
  restAPIKey: string,
  cognitoUser: User
): Promise<void> => {
  const stageConfig = findStageConfiguration(stageName)
  const key = `${poolAddr}/${fileName}`
  const accessToken = cognitoUser?.access_token

  const result = await axios.post(
    `${stageConfig.serverApiBaseUrl}/deleteFromS3Presigned`,
    {
      fileName: key,
    },
    {
      headers: {
        'x-api-key': restAPIKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  )

  // For direct delete, we expect a success response, not a presigned URL
  if (result.status !== 200) {
    throw new Error(`Delete failed with status ${result.status}`)
  }
}
